import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cartApiService } from 'api/services'
import { IProduct, IProductAmount, IUseAmount } from 'api/types/product'
import { QUERY_KEY } from 'app/constants'
import { AxiosError, AxiosResponse } from 'axios'
import { toastError } from 'components/toaster'
import { useTranslation } from 'react-i18next'
import { useUser } from './use-user'
import { useDebouncedUrlFilters } from 'lib/hooks/use-debounced-url-filters'
import { useUrlQuery } from 'lib/hooks/use-url-query'

export const useAmount = (): IUseAmount => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useUser()
  const urlFilters = useDebouncedUrlFilters()
  const { urlQuery } = useUrlQuery()

  const {
    mutate: amountMutation,
    isPending,
    isError,
  } = useMutation({
    mutationKey: [QUERY_KEY.amount],
    mutationFn: (amount: IProductAmount) => cartApiService.setAmount(amount),
    onMutate: ({ id, subCategory, amount }) => {
      const oldProducts = queryClient.getQueryData<
        AxiosResponse<Array<IProduct>>
      >([
        QUERY_KEY.products,
        {
          userId: user?.id ?? null,
          id: subCategory,
          filters: urlFilters,
          search: urlQuery?.id ?? '',
        },
      ])

      queryClient.setQueryData(
        [
          QUERY_KEY.products,
          {
            userId: user?.id ?? null,
            id: subCategory,
            filters: urlFilters,
            search: urlQuery?.id ?? '',
          },
        ],
        (oldData?: InfiniteData<AxiosResponse<Array<IProduct>>>) =>
          oldData && {
            ...oldData,
            pages: oldData.pages.map((item) => ({
              ...item,
              data: item.data.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    amount,
                  }
                }

                return item
              }),
            })),
          },
      )

      return oldProducts
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          QUERY_KEY.cart,
          {
            userId: user?.id ?? null,
            language: i18n.language,
          },
        ],
        data,
      )
    },
    onError: (error: AxiosError, variable, context) => {
      if (!context) return
      if (error.response) {
        toastError(t(`${error.response.data.error}`))
      }

      queryClient.setQueryData(
        [
          QUERY_KEY.products,
          {
            userId: user?.id ?? null,
            id: variable.subCategory,
            filters: urlFilters,
            search: urlQuery?.id ?? '',
          },
        ],
        context,
      )
    },
  })

  return {
    amountMutation,
    isPending,
    isError,
  }
}
